/**
 * Agent Dashboard Page
 *
 * Displays a task queue of open visits assigned to the authenticated agent.
 * Uses cookie-based session auth (magic link flow), separate from JWT form auth.
 */

import {
  Banner,
  Button,
  ButtonLink,
  DescriptionList,
  Form,
  formatDate,
  formatLabel,
  formatPlural,
  Heading,
  Input,
  NegativeTag,
  PositiveTag,
  Progress,
  Tag,
  WarningTag,
} from '@ox/blox';
import type { AgentDashboardVisit, AgentDashboardVisitsResponse } from '@ox/ox-brokerage-client';
import { agentDashboardApi } from '@ox/visits-ui/clients/mtx';
import '@ox/visits-ui/components/pages/AgentDashboardPage.scss';
import { Header } from '@ox/visits-ui/components/shared/Header';
import { useDashboardAuth } from '@ox/visits-ui/hooks/useDashboardAuth';
import type { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';

const LoginView: FunctionalComponent<{
  emailSent: boolean;
  error: string | null;
  onSendEmail: (email: string) => Promise<void>;
}> = ({ emailSent, error, onSendEmail }) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const handleEmailChange = useCallback((e: Event) => {
    setEmail((e.target as HTMLInputElement).value);
  }, []);

  const handleSubmit = useCallback(
    async (e: Event) => {
      e.preventDefault();
      if (!email.trim()) {
        return;
      }
      setSending(true);
      await onSendEmail(email.trim());
      setSending(false);
    },
    [email, onSendEmail],
  );

  if (emailSent) {
    return (
      <div className="dashboard-login">
        <div className="login-content">
          <Heading level="h1">Check your email</Heading>
          <p>
            We sent a login link to <strong>{email}</strong>. Click it to access your dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-login">
      <div className="login-content">
        <Heading level="h1">Agent Dashboard</Heading>
        <p>Enter your email to receive a login link.</p>
        <Form className="login-form" onSubmit={handleSubmit}>
          <Input
            label="Email address"
            type="email"
            name="email"
            placeholder="agent@example.com"
            value={email}
            onInput={handleEmailChange}
            required
          />
          <Button type="submit" disabled={sending || !email.trim()}>
            {sending ? 'Sending...' : 'Send me a login link'}
          </Button>
        </Form>
        {error && <Banner theme="negative">{error}</Banner>}
      </div>
    </div>
  );
};

function FormStatusTag({ status }: { status: string }) {
  switch (status) {
    case 'in_progress':
      return <WarningTag>In progress</WarningTag>;
    case 'complete':
      return <PositiveTag>Complete</PositiveTag>;
    case 'expired':
      return <NegativeTag>Expired</NegativeTag>;
    case 'not_assigned':
    case 'assigned':
      return <Tag>Not started</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
}

function isOverdue(dueBy: Date | undefined): boolean {
  if (!dueBy) {
    return false;
  }
  return dueBy < new Date();
}

const VisitCard: FunctionalComponent<{ visit: AgentDashboardVisit }> = ({ visit }) => {
  const overdue = isOverdue(visit.dueBy) && visit.formStatus !== 'complete';
  const visibleAccessEntries = (visit.accessEntries ?? []).filter((e) => e.code || e.location || e.note);

  return (
    <div className="visit-card">
      <div className="visit-card-header">
        <span className="visit-address">{visit.listingAddress || 'No address'}</span>
        {visit.visitType && <span className="visit-type-badge">{visit.visitType}</span>}
      </div>
      {overdue && visit.dueBy && (
        <Banner theme="negative">
          This visit was due on {formatDate(visit.dueBy, 'MM/DD/YYYY')}. Please complete this form
          as soon as possible.
        </Banner>
      )}
      <DescriptionList
        termTypography="label-small"
        descriptionTypography="paragraph-small"
        orientation="horizontal"
        items={{
          ...(visit.formStatus ? { 'Form': <FormStatusTag status={visit.formStatus} /> } : {}),
          ...(visit.dueBy ? { 'Due': formatDate(visit.dueBy, 'MM/DD/YYYY') } : {}),
          'Created': formatDate(visit.createdAt, 'MM/DD/YYYY'),
        }}
      />
      {visibleAccessEntries.length > 0 && (
        // eslint-disable-next-line react/forbid-elements -- no BLOX Details export exists
        <details className="access-info">
          <summary>Access Info</summary>
          <div className="access-entries">
            {visibleAccessEntries.map((entry, idx) => (
              <DescriptionList
                // eslint-disable-next-line react/no-array-index-key -- access entries have no unique ID
                key={`${entry.type}-${entry.entryMethod}-${idx}`}
                termTypography="label-small"
                descriptionTypography="paragraph-small"
                orientation="horizontal"
                items={{
                  'Type': formatLabel(entry.type ?? ''),
                  ...(entry.entryMethod ? { 'Entry Method': formatLabel(entry.entryMethod) } : {}),
                  ...(entry.location ? { 'Location': entry.location } : {}),
                  ...(entry.code ? { 'Code': entry.code } : {}),
                  ...(entry.note ? { 'Note': entry.note } : {}),
                }}
              />
            ))}
          </div>
        </details>
      )}
      {visit.formLink && (
        <ButtonLink size="small" href={visit.formLink}>
          Open Form
        </ButtonLink>
      )}
    </div>
  );
};

const DashboardView: FunctionalComponent<{ data: AgentDashboardVisitsResponse }> = ({ data }) => (
  <div className="agent-dashboard-page">
    <Header />
    <div className="dashboard-content">
      <div className="dashboard-greeting">
        <Heading level="h1">Your Visits</Heading>
        <p>
          Welcome, {data.agentName}. You have {data.total} open{' '}
          {formatPlural(data.total, 'visit', 'visits')}.
        </p>
        {data.resourcesGuideUrl && (
          <ButtonLink size="small" hierarchy="secondary" href={data.resourcesGuideUrl} target="_blank">
            View Resources Guide
          </ButtonLink>
        )}
      </div>

      {data.visits.length === 0 ? (
        <div className="empty-state">
          <p>No open visits assigned to you right now.</p>
        </div>
      ) : (
        <div className="visit-list">
          {data.visits.map((visit) => (
            <VisitCard key={visit.visitId} visit={visit} />
          ))}
        </div>
      )}
    </div>
  </div>
);

export const AgentDashboardPage: FunctionalComponent = () => {
  const {
    isLoading: authLoading,
    isAuthenticated,
    error: authError,
    emailSent,
    sendLoginEmail,
  } = useDashboardAuth();
  const [visitsData, setVisitsData] = useState<AgentDashboardVisitsResponse | null>(null);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsError, setVisitsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    async function fetchVisits() {
      setVisitsLoading(true);
      try {
        const data = await agentDashboardApi.getAgentVisitsApiV1PublicAgentVisitsGet();
        if (!cancelled) {
          setVisitsData(data);
        }
      } catch {
        if (!cancelled) {
          setVisitsError('Failed to load visits. Please try again later.');
        }
      } finally {
        if (!cancelled) {
          setVisitsLoading(false);
        }
      }
    }

    fetchVisits();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (authLoading) {
    return (
      <div className="dashboard-loading">
        <Progress label="Loading..." labelPosition="after" type="circular" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginView emailSent={emailSent} error={authError} onSendEmail={sendLoginEmail} />;
  }

  if (visitsLoading) {
    return (
      <div className="dashboard-loading">
        <Progress label="Loading visits..." labelPosition="after" type="circular" />
      </div>
    );
  }

  if (visitsError) {
    return (
      <div className="agent-dashboard-page">
        <Header />
        <div className="dashboard-content">
          <Banner theme="negative">{visitsError}</Banner>
        </div>
      </div>
    );
  }

  if (visitsData) {
    return <DashboardView data={visitsData} />;
  }

  return (
    <div className="dashboard-loading">
      <Progress label="Loading visits..." labelPosition="after" type="circular" />
    </div>
  );
};
